import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { inject, observer } from "mobx-react";

import strings from "../../lang/strings";
import { SidebarMenu, ExtraRoutes } from "../../config/page_route";
import AccessDenied from "../common/AccessDenied";
import {
  hasPermission,
  getFirstAccessibleRoute,
} from "@/utils/routePermission";

const Content = ({ rootStore }: { rootStore?: any }) => {
  const location = useLocation();
  const isCorpMode = rootStore.getCorpMode;
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  // 사용자 정보가 변경될 때마다 업데이트
  const userRole = rootStore.getProfile?.role ?? null;
  const userPermissions = rootStore.getPermissions || [];
  const userType = rootStore.getProfile?.userType;

  // 권한 체크 함수 (공통 유틸리티 사용)
  const checkPermission = useCallback(
    (route: any) => {
      return hasPermission(route, userRole, userPermissions, userType);
    },
    [userRole, userPermissions, userType]
  );

  // 기본 라우트 결정 함수 (공통 유틸리티 사용)
  const getDefaultRoute = useCallback(() => {
    return getFirstAccessibleRoute(userRole, userPermissions, userType);
  }, [userRole, userPermissions, userType]);

  // 인증 상태 초기화 확인
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // localStorage에서 토큰이 있는지 확인
        const adminData = localStorage.getItem("admin");
        const refreshToken = localStorage.getItem("refresh_token");
        const userInfo = localStorage.getItem("user_info");

        if (adminData || refreshToken) {
          // 토큰이 있으면 사용자 정보 복원
          if (userInfo) {
            try {
              const parsedUserInfo = JSON.parse(userInfo);
              rootStore.setProfile(parsedUserInfo);
            } catch (error) {
              console.error("사용자 정보 파싱 실패:", error);
              // 파싱 실패 시 해당 데이터 삭제
              localStorage.removeItem("user_info");
            }
          }

          // 토큰이 있으면 인증 초기화 완료로 간주
          setIsAuthInitialized(true);
        } else {
          // 토큰이 없으면 로그인 페이지로 이동
          setIsAuthInitialized(true);
        }
      } catch (error) {
        console.error("인증 초기화 실패:", error);
        setIsAuthInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // 타이틀 설정 로직
  const setTitle = (pathname: string, isCorpMode: boolean) => {
    const defaultTitle = "카리아카";

    // 기관 모드와 클래스 모드에 따라 적절한 메뉴 선택
    const mainMenus = SidebarMenu;
    const extraMenus = ExtraRoutes;

    // 모든 메뉴 아이템을 하나의 배열로 합침
    const allMenuItems = [
      ...extraMenus,
      ...mainMenus.flatMap((menu) => menu.children),
    ];

    // 현재 경로와 일치하는 메뉴 아이템 찾기
    // 더 구체적인 경로가 우선적으로 매칭되도록 정렬
    const sortedMenuItems = [...allMenuItems].sort((a, b) => {
      // 정확한 경로 일치가 우선
      if (pathname === a.path && pathname !== b.path) return -1;
      if (pathname === b.path && pathname !== a.path) return 1;

      // containPath 길이가 긴 것이 우선 (더 구체적인 경로)
      const aContainPathLength = a.containPath
        ? Math.max(...a.containPath.map((p) => p.length))
        : 0;
      const bContainPathLength = b.containPath
        ? Math.max(...b.containPath.map((p) => p.length))
        : 0;

      return bContainPathLength - aContainPathLength;
    });

    const matched = sortedMenuItems.find((menu) => {
      // 정확한 경로 일치 확인
      const matchByPath = pathname === menu.path;

      // containPath 배열이 있는 경우, 해당 경로로 시작하는지 확인
      const matchByContain = menu.containPath?.some((p) =>
        pathname.startsWith(p)
      );

      return matchByPath || matchByContain;
    });

    // 페이지 타이틀 설정
    const pageTitle = matched?.title || defaultTitle;
    const fullTitle = `${pageTitle}`;
    document.title = fullTitle;
  };

  // 페이지 이동 감지 시 타이틀 설정
  useEffect(() => {
    setTitle(location.pathname, rootStore.getCorpMode);
  }, [location.pathname, rootStore.getCorpMode]);

  // 부모 메뉴 클릭 시 첫 번째 자식 메뉴로 리다이렉트
  const handleParentRoute = (route) => {
    if (route.children && route.children.length > 0) {
      const firstChild = route.children[0];
      return <Navigate to={firstChild.path} replace />;
    }
    return null;
  };

  // 인증이 초기화되지 않았으면 로딩 상태
  if (!isAuthInitialized) {
    return <></>;
  }

  // 라우트 렌더링
  return (
    <Routes>
      {/* Main Menu Routes */}
      {SidebarMenu.map((route, index) => (
        <React.Fragment key={`main-menu-${index}`}>
          {/* 부모 메뉴 라우트 */}
          <Route
            path={route.path}
            element={
              !route.private || rootStore.isSignedIn ? (
                checkPermission(route) ? (
                  handleParentRoute(route)
                ) : (
                  <AccessDenied />
                )
              ) : (
                <Navigate to="/login" state={{ from: location }} replace />
              )
            }
          />
          {/* 자식 메뉴 라우트들 */}
          {route.children.map((subRoute, subIndex) => (
            <Route
              key={`main-menu-${index}-${subIndex}`}
              path={subRoute.path}
              element={
                !subRoute.private || rootStore.isSignedIn ? (
                  checkPermission(subRoute) ? (
                    <subRoute.component />
                  ) : (
                    <AccessDenied />
                  )
                ) : (
                  <Navigate to="/login" state={{ from: location }} replace />
                )
              }
            />
          ))}
        </React.Fragment>
      ))}
      {/* Extra Routes */}
      {ExtraRoutes.map((route, index) => (
        <Route
          key={`extra-route-${index}`}
          path={route.path}
          element={
            !route.private || rootStore.isSignedIn ? (
              checkPermission(route) ? (
                <route.component />
              ) : (
                <AccessDenied />
              )
            ) : (
              <Navigate to="/login" state={{ from: location }} replace />
            )
          }
        />
      ))}
      {/* 루트 경로 처리 */}
      <Route
        path="/"
        element={
          rootStore.isSignedIn ? (
            <Navigate to={getDefaultRoute()} replace />
          ) : (
            <Navigate to="/login" state={{ from: location }} replace />
          )
        }
      />
      <Route
        path="*"
        element={<Navigate to="/login" state={{ from: location }} replace />}
      />
    </Routes>
  );
};

export default inject("rootStore")(observer(Content));
